import _ from 'lodash';
import moment from 'moment';
import { setDirtyForm } from 'app/actions/dirty-forms.actions';
import { checkAccess } from 'app/actions/app.actions';
import { fetchPopup } from 'app/actions/login-popup.actions';
import * as api from 'app/resources/login-popup.api';
import appHelpers from 'app/app-helpers';
import popupManagementTemplate from './cms-popup-management.html';

const DIRTY_FORM = 'loginPopupsManagement';

const mapStateToThis = ({
  session,
  config,
  ui,
  loginPopup,
  dirtyForms,
}) => {
  const { locale, lotteryTime, lotteryTimezoneDiff } = session;
  const {
    locales,
    ckeditor,
    secondHandApproval,
    jurisdictionId,
  } = config;
  const { selectedLanguage } = ui;
  const {
    current,
    properties,
    draft,
  } = loginPopup;
  const ckeditorCreatePopup = {
    extraPlugins: 'wordcount',
    wordcount: {
      showParagraphs: false,
      showWordCount: false,
      showCharCount: true,
      countSpacesAsChars: true,
      countHTML: false,
    },
  };
  const ckeditorConfig = { ...ckeditor, ...ckeditorCreatePopup };

  return {
    locale,
    locales,
    lotteryTime,
    lotteryTimezoneDiff,
    selectedLanguage,
    current,
    draft,
    properties,
    ckeditorConfig,
    secondHandApproval,
    jurisdictionId,
    dirtyForm: dirtyForms[DIRTY_FORM],
  };
};

const mapDispatchToThis = {
  fetchPopup,
  setDirtyForm,
  checkAccess,
};

// eslint-disable-next-line no-undef
@Inject('$ngRedux', 'Notifications', 'gettextCatalog')
class CmsLoginPopupManagementController {
  constructor() {
    this.unsubscribe = this.$ngRedux.connect(mapStateToThis, mapDispatchToThis)(this);
    this.DIRTY_FORM = DIRTY_FORM;
  }

  async $onInit() {
    await this.initPopup();
    this.secondHandApprovalActive = this.checkAccess('cms.loginPopupManagement.approve');
    this.dateRangeError = false;
  }

  $onDestroy() {
    this.unsubscribe();
  }

  async initPopup() {
    await this.fetchPopup();
    this.copyItems();
    this.setEntries(this.locale);
  }

  copyItems() {
    this.currentCopy = _.cloneDeep(this.current);
    this.propertiesCopy = _.cloneDeep(this.properties);
    this.propertiesCopy.dateFrom = this.setDate(this.propertiesCopy.dateFrom);
    this.propertiesCopy.dateTo = this.setDate(this.propertiesCopy.dateTo);
    this.propertiesCopy.timeFrom = this.setTime(this.propertiesCopy.timeFrom);
    this.propertiesCopy.timeTo = this.setTime(this.propertiesCopy.timeTo);
  }

  setDate(date) {
    if (date) return moment(date).toDate();
    return moment.unix(this.lotteryTime).toDate();
  }

  setTime(time) {
    if (time) return moment(time, 'HH:mm').toDate();
    return moment().toDate();
  }

  setEntries(locale) {
    this.language = locale;
    const key = Object.keys(this.currentCopy).find((localeKey) => localeKey === locale);
    if (!key) {
      this.currentCopy[locale] = {};
    }
  }

  async discardChanges(form) {
    await api.discardPopup(this.jurisdictionId);
    await this.initPopup();
    form.$setPristine();
    this.setDirtyForm(this.DIRTY_FORM, false);
    this.Notifications.alertSuccess(this.gettextCatalog.getString('Changes have been discarded'));
  }

  cancel(form) {
    this.copyItems();
    form.$setPristine();
    this.setDirtyForm(this.DIRTY_FORM, false);
    this.Notifications.alertSuccess(this.gettextCatalog.getString('Changes have been canceled'));
  }

  publishChanges(form) {
    form.$setSubmitted();
    this.validateForm();
    if (form.$valid && !this.timeFromError && !this.timeToError && !this.dateRangeError) {
      const entriesToSave = [{
        ...this.currentCopy,
        fields: appHelpers.setFieldsToEntry(this.currentCopy.fields),
      }];
      const propertiesToSave = {
        ...this.propertiesCopy,
        dateFrom: moment.utc(this.propertiesCopy.dateFrom).format('x'),
        dateTo: moment.utc(this.propertiesCopy.dateTo).format('x'),
        timeFrom: moment(this.propertiesCopy.timeFrom).format('HH:mm'),
        timeTo: moment(this.propertiesCopy.timeTo).format('HH:mm'),
      };

      api.publishPopup(
        this.secondHandApproval,
        this.jurisdictionId,
        entriesToSave,
        propertiesToSave,
      )
        .then(() => this.Notifications.alertSuccess(this.getSuccessText()))
        .then(() => this.initPopup())
        .then(() => this.setDirtyForm(DIRTY_FORM, false))
        .catch((e) => console.log(e));
    }
  }

  validateForm() {
    const lotteryTime = moment.unix(this.lotteryTime).utc().format('l HH:mm');
    const dateTimeFrom = `${moment.utc(this.propertiesCopy.dateFrom).format('l')} ${moment(this.propertiesCopy.timeFrom).format('HH:mm')}`;
    const dateTimeTo = `${moment.utc(this.propertiesCopy.dateTo).format('l')} ${moment(this.propertiesCopy.timeTo).format('HH:mm')}`;

    this.timeFromError = !moment(this.propertiesCopy.timeFrom).isValid();
    this.timeToError = !moment(this.propertiesCopy.timeTo).isValid();
    this.dateRangeError = this.propertiesCopy.type === 'range'
      && (moment(dateTimeFrom, 'l HH:mm').isAfter(moment(dateTimeTo, 'l HH:mm'))
      || moment(lotteryTime, 'l HH:mm').isAfter(moment(dateTimeFrom, 'l HH:mm')));
  }

  getSuccessText() {
    return this.isSecondHandApproval()
      ? this.gettextCatalog.getString('Changes have been published')
      : this.gettextCatalog.getString('Changes have been submitted for approval');
  }

  isSecondHandApproval() {
    return this.secondHandApprovalActive || !this.secondHandApproval;
  }

  isDiscardChangesAvailable() {
    return this.secondHandApproval && this.secondHandApprovalActive && this.draft;
  }

  isSubmitForApprovalAvailable() {
    return this.secondHandApproval && !this.secondHandApprovalActive && this.dirtyForm;
  }

  isPublishChangesAvailable() {
    return (!this.secondHandApproval && this.dirtyForm)
      || (this.secondHandApproval && this.secondHandApprovalActive && this.draft)
      || (this.secondHandApproval && this.secondHandApprovalActive && this.dirtyForm);
  }

  previewPopup() {
    this.Notifications.showAlert('', this.currentCopy.fields[this.language].content);
  }
}

export default class CmsLoginPopupManagementComponent {
  constructor() {
    this.controller = CmsLoginPopupManagementController;
    this.template = popupManagementTemplate;
    return this;
  }
}



// WEBPACK FOOTER //
// ./src/modules/app/components/home/management/components/login-popups/cms-popup-management-component.js